// Shared user account menu (Avatar → Meine Daten, Vereine, Logout, …).
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  UserCircle2,
  LogOut,
  PlusCircle,
  CreditCard,
  FlaskConical,
} from "lucide-react";

function read(t, key, fallback = "") {
  const v = typeof t === "function" ? t(key) : key;
  return (v && v !== key ? v : fallback || key || "").toString();
}

function __asStr(v) {
  return String(v ?? "").trim();
}

/**
 * @param {{
 *   t: Function,
 *   authUserLabel?: string,
 *   authUserEmail?: string,
 *   onSignOut?: () => void,
 *   canAccessAccessRequestsAdmin?: boolean,
 *   canAccessVereinsabo?: boolean,
 *   shellAccessRestricted?: boolean,
 *   coachDeskSelfProfileHref?: string,
 *   homeMode?: boolean,
 *   userClubs?: Array<{ id: string, label?: string }>|null,
 *   launcherAccess?: { isSuperAdmin?: boolean }|null,
 *   showLegalLinks?: boolean,
 *   onRequestClose?: () => void,
 * }} props
 */
export function ShellUserMenuPanel({
  t,
  authUserLabel = "",
  authUserEmail = "",
  onSignOut,
  canAccessAccessRequestsAdmin = false,
  canAccessVereinsabo = false,
  shellAccessRestricted = false,
  coachDeskSelfProfileHref = "",
  homeMode = false,
  userClubs = null,
  launcherAccess = null,
  showLegalLinks = false,
  onRequestClose,
}) {
  const navigate = useNavigate();
  const menuLabel = read(t, "nav.menu");
  const myDataLabel = read(t, "appTopbar.menu.myData");
  const myProfileLabel = read(t, "hub.userMenu.profile");
  const myClubsLabel = read(t, "hub.userMenu.myClubs");
  const signOutLabel = read(t, "auth.signOut");
  const signedInAs = read(t, "auth.signedInAs");
  const menuAccountEmail = __asStr(authUserEmail);
  const menuAccountName = __asStr(authUserLabel);
  const showMyDataMenuLabel = Boolean(__asStr(coachDeskSelfProfileHref));
  const profileMenuLabel = showMyDataMenuLabel ? myDataLabel : myProfileLabel;
  const safeUserClubs = Array.isArray(userClubs)
    ? userClubs.filter((c) => c && c.id)
    : [];
  const connectClubLabel = read(
    t,
    safeUserClubs.length > 0
      ? "hub.userMenu.connectAnotherClub"
      : "hub.userMenu.connectClub",
  );

  const close = (event) => {
    const detailsEl = event?.currentTarget?.closest?.("details");
    if (detailsEl && typeof detailsEl.removeAttribute === "function") {
      detailsEl.removeAttribute("open");
    }
    if (typeof onRequestClose === "function") onRequestClose();
  };

  const handleConnectClub = (event) => {
    close(event);
    if (homeMode && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("tm:hub-connect-club"));
      return;
    }
    navigate("/profile/kontaktdaten");
  };

  return (
    <div className="app-topbar__menuPanel" role="menu" aria-label={menuLabel}>
      <div className="app-topbar__menuSection app-topbar__menuSection--account">
        <div className="app-topbar__menuHead app-topbar__menuHead--profile">
          <div className="app-topbar__menuHeadText">
            {menuAccountName ? (
              <strong className="app-topbar__menuPersonName">
                {menuAccountName}
              </strong>
            ) : null}
            {menuAccountEmail ? (
              <span className="muted app-topbar__menuPersonEmail">
                {menuAccountEmail}
              </span>
            ) : null}
            {!menuAccountName && !menuAccountEmail ? (
              <div className="muted app-topbar__menuMeta">
                {signedInAs}: {authUserLabel}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {!shellAccessRestricted ? (
        <div className="app-topbar__menuSection app-topbar__menuSection--primary">
          <button
            type="button"
            className="btn btn--ghost btn--sm app-topbar__menuAction"
            onClick={(e) => {
              close(e);
              navigate("/profile/kontaktdaten");
            }}
          >
            <UserCircle2 size={16} strokeWidth={2} aria-hidden="true" />
            <span>{profileMenuLabel}</span>
          </button>
        </div>
      ) : null}
      {!shellAccessRestricted && safeUserClubs.length > 0 ? (
        <div className="app-topbar__menuSection app-topbar__menuSection--clubs">
          <div className="app-topbar__menuSectionTitle">{myClubsLabel}</div>
          <ul className="app-topbar__menuClubsList">
            {safeUserClubs.map((club) => (
              <li
                key={club.id}
                className="app-topbar__menuClub"
                title={club.label}
              >
                <Building2 size={14} strokeWidth={2} aria-hidden="true" />
                <span>{club.label}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {!shellAccessRestricted ? (
        <div className="app-topbar__menuSection">
          <button
            type="button"
            className="btn btn--ghost btn--sm app-topbar__menuAction"
            onClick={handleConnectClub}
          >
            <PlusCircle size={16} strokeWidth={2} aria-hidden="true" />
            <span>{connectClubLabel}</span>
          </button>
        </div>
      ) : null}
      {!shellAccessRestricted && canAccessVereinsabo ? (
        <div className="app-topbar__menuSection">
          <button
            type="button"
            className="btn btn--ghost btn--sm app-topbar__menuAction"
            onClick={(e) => {
              close(e);
              navigate("/trainingsdesk/stammdaten/vereinsabo");
            }}
          >
            <CreditCard size={16} strokeWidth={2} aria-hidden="true" />
            <span>{read(t, "hub.userMenu.vereinsabo")}</span>
          </button>
        </div>
      ) : null}
      {!shellAccessRestricted && canAccessAccessRequestsAdmin ? (
        <div className="app-topbar__menuSection">
          <button
            type="button"
            className="btn btn--ghost btn--sm app-topbar__menuAction"
            onClick={(e) => {
              close(e);
              navigate("/access-requests-admin");
            }}
          >
            {read(t, "hub.accessRequests.title")}
          </button>
        </div>
      ) : null}
      {!shellAccessRestricted && launcherAccess?.isSuperAdmin ? (
        <div className="app-topbar__menuSection">
          <button
            type="button"
            className="btn btn--ghost btn--sm app-topbar__menuAction"
            onClick={(e) => {
              close(e);
              navigate("/superadmin-clubs");
            }}
          >
            {read(t, "superadmin.tiles.clubs.title")}
          </button>
        </div>
      ) : null}
      {import.meta.env.DEV &&
      !shellAccessRestricted &&
      launcherAccess?.isSuperAdmin ? (
        <div className="app-topbar__menuSection">
          <button
            type="button"
            className="btn btn--ghost btn--sm app-topbar__menuAction"
            onClick={(e) => {
              close(e);
              navigate("/dev/qa");
            }}
          >
            <FlaskConical size={16} strokeWidth={2} aria-hidden="true" />
            <span>{read(t, "dev.qa.menuLabel")}</span>
          </button>
        </div>
      ) : null}

      {showLegalLinks ? (
        <div className="app-topbar__menuSection app-topbar__menuSection--legal">
          <a
            className="app-topbar__menuLegalLink"
            href="/impressum"
            onClick={close}
          >
            {read(t, "footer.impressum", "Impressum")}
          </a>
          <a
            className="app-topbar__menuLegalLink"
            href="/datenschutz"
            onClick={close}
          >
            {read(t, "footer.privacy", "Datenschutz")}
          </a>
        </div>
      ) : null}

      <div className="app-topbar__menuSection app-topbar__menuSection--logout">
        <button
          type="button"
          className="btn btn--ghost btn--sm app-topbar__menuAction app-topbar__menuAction--danger"
          onClick={(e) => {
            close(e);
            if (typeof onSignOut === "function") onSignOut();
          }}
        >
          <LogOut size={16} strokeWidth={2} aria-hidden="true" />
          <span>{signOutLabel}</span>
        </button>
      </div>
    </div>
  );
}
